/**
 * Exceptions-first rules for the Roost Control Center dashboard (WI 34293).
 * Pure: normalized telemetry in, alerts out. No Angular, no I/O. The inputs are
 * built by attention-inputs.logic.ts from the bridge and, inside the Electron
 * Control Center, from window.rynCC.attention().
 */
import { UnansweredInbound, formatDuration } from './wallboard.logic';
import { waitingAlerts } from './waiting.logic';
import { daemonAlerts } from './daemon-alerts.logic';
import { type SeatLogin, seatLoginAlerts } from './seat-login-alerts.logic';
import type { DaemonRow } from './daemons.logic';
import {
  type DesiredRosterHost,
  type RosterSeatRow,
  seatDownAlerts,
} from './desired-roster.logic';
import { type OwedOccurrence, owedDutyAlerts } from './owed-duties.logic';
import {
  type SeatSessionLink,
  type SeatSessionSnapshot,
  seatSessionLinks,
} from './seat-session.logic';
import type { SeatWorkQueue } from '../models/work-queue.model';
import {
  FIVE_HOUR_MS,
  PaceBasis,
  PaceSample,
  WEEK_MS,
  WindowKey,
  actOnLevel,
  projectPace,
  rateBasisText,
} from './pace.logic';

// ── Thresholds (Tim, WI 34293 acceptance criteria) ──────────────────────────

/** Seat context strictly over this is a warning. */
export const CONTEXT_WARN_PCT = 60;
/** Seat context at or over this is critical. */
export const CONTEXT_CRIT_PCT = 80;
/** Host memory or disk at or over this is a warning. */
export const HOST_WARN_PCT = 85;
/** Host memory or disk at or over this is critical. */
export const HOST_CRIT_PCT = 92;
/** A host with seats that has not reported for longer than this is silent. */
export const HOST_LIVENESS_MS = 10 * 60_000;
/** A prompt with no progress for this long means the seat is stuck on it. */
export const PROMPT_STUCK_MS = 2 * 60_000;

// ── Normalized inputs ───────────────────────────────────────────────────────

export type SeatState = 'logged-out' | 'limit-reached' | 'prompt' | null;

export interface SeatSignal {
  instanceId: string;
  host: string | null;
  /** The account the seat rides; null when unknown. */
  account: string | null;
  /** Context window used, percent. Null when no reading exists. */
  ctxPct: number | null;
  /** Local CC pane view, else the seat's own fresh bridge seatStatus (US 37271). */
  state: SeatState;
  stateDetail: string | null;
  /** Minutes since the seat last made progress (statusline rewrite). */
  idleMinutes: number | null;
  /** True when the seat runs on this Control Center's machine. */
  local: boolean;
  login?: SeatLogin | null; // WI 34592: signed-in account vs registry login
}

export interface HostSignal {
  host: string;
  seatIds: string[];
  /** True when seatIds comes from last-known placement rather than a
   * currently-fresh context snapshot (WI 34272 review, P1): a host must
   * never silently read as zero seats just because its placement snapshot
   * aged past SEAT_FRESH_MS while it keeps reporting live host metrics. */
  seatsStale: boolean;
  /** Freshest placement observation for this host's seats, or null when no
   * placement was ever seen (a metrics-only host). */
  seatsLastObservedAtMs: number | null;
  /** Newest report from this host (metrics sample or seat snapshot). */
  lastReportAtMs: number | null;
  cpuPct: number | null;
  memPct: number | null;
  diskPct: number | null;
  diskFreeBytes: number | null;
}

// One definition (pace.logic), re-exported so existing imports keep working.
export type { WindowKey } from './pace.logic';

export interface AccountWindowSignal {
  window: WindowKey;
  percent: number;
  resetAtMs: number;
  samples: PaceSample[];
}

export interface AccountSignal {
  accountUpn: string;
  seatIds: string[];
  windows: AccountWindowSignal[];
}

export interface AttentionInput {
  seats: SeatSignal[];
  hosts: HostSignal[];
  accounts: AccountSignal[];
  /** User inbounds open past 2 minutes (WI 34329); optional for older callers. */
  waiting?: UnansweredInbound[];
  /** Expected daemons not running, from the CC daemons reports (WI 34278). */
  daemons?: DaemonRow[];
  /** Bridge work-queue rows, for seats whose process exited (Bug 34685). */
  deadSeats?: SeatWorkQueue[];
  /** WI 34807: host rosters, and retired rows (auto-retired = down, sticky = not). */
  desiredRoster?: DesiredRosterHost[];
  retiredSeats?: RosterSeatRow[];
  /** Scheduled duties owed past 30 min, from the bridge (WI 35459). */
  owed?: OwedOccurrence[];
  /** Context snapshots, for the waiting rows' terminal links (WI 34335). */
  sessions?: readonly SeatSessionSnapshot[] | null;
}

// ── Alerts ──────────────────────────────────────────────────────────────────

export type Severity = 'crit' | 'warn';
/** The ticket's three alert kinds, plus a user waiting on a seat. */
export type AlertKind = 'seat' | 'account' | 'machine' | 'user';
/** The tile an alert counts toward (seat splits into down, owed duty and
 * context). */
export type AlertCategory =
  'waiting' | 'down' | 'owed' | 'context' | 'account' | 'machine';
export type ActionType =
  | 'log-in'
  | 'open-seat'
  | 'cycle-seat'
  | 'view-account'
  | 'open-machine'
  | 'open-chat';

export interface AlertAction {
  type: ActionType;
  label: string;
  /** Seat instance id, account UPN, host name, or a chat URL (open-chat). */
  target: string;
}

export interface Alert {
  id: string;
  severity: Severity;
  kind: AlertKind;
  category: AlertCategory;
  title: string;
  detail: string;
  action: AlertAction;
  /** Tie-break inside a severity; bigger is worse. */
  weight: number;
  /** A parked thread: listed, but not a live wait, so not counted. */
  parked?: boolean;
  /** An open-inbound row Tim can dismiss: resolves it on the bridge (Bug 35644). */
  dismiss?: { chatId: string; receivedAtMs: number };
  /** A waiting row's link to the seat's own terminal (WI 34335). */
  session?: SeatSessionLink;
  /** `session` as /terminal query params, for the template's routerLink. */
  sessionQuery?: Record<string, string>;
}

// ── Account projection ──────────────────────────────────────────────────────

export interface WindowProjection {
  window: WindowKey;
  percent: number;
  /** Percent at reset at the current burn; null with no burn rate yet. */
  projectedPct: number | null;
  resetAtMs: number;
  /** When it hits 100% at the current burn; null if never before reset. */
  hitsAtMs: number | null;
  level: Severity | null;
  /** Where the burn rate came from (Bug 34656 marks 'window-average'). */
  basis: PaceBasis;
  /** The burn rate the projection used, in points per hour (Bug 34688). */
  burnPctPerHour: number | null;
  /** Span of the samples behind a `recent` rate (30 min+ for 5h, 12 h+ for 7d). */
  recentSpanMs: number | null;
}

/**
 * Current used% + burn rate x time to reset, then `actOnLevel` (Bug 34652),
 * the same rule for 5h and 7d: critical if it reaches 100% before reset,
 * warning when projected at `ACT_ON_PROJECTED_PCT`+, otherwise none.
 */
export function projectWindow(
  w: AccountWindowSignal,
  nowMs: number
): WindowProjection {
  const windowMs = w.window === '5h' ? FIVE_HOUR_MS : WEEK_MS;
  const pace = projectPace(w.percent, w.resetAtMs, windowMs, nowMs, w.samples);
  const hitsAtMs =
    w.percent >= 100 ? nowMs : pace.fillsBeforeReset ? pace.fillAtMs : null;
  const projectedPct =
    w.percent >= 100 ? w.percent : (pace.projectedAtReset ?? null);
  const level: Severity | null = actOnLevel(projectedPct, hitsAtMs !== null);
  return {
    window: w.window,
    percent: w.percent,
    projectedPct,
    resetAtMs: w.resetAtMs,
    hitsAtMs,
    level,
    basis: pace.basis,
    burnPctPerHour: pace.burnPctPerHour,
    recentSpanMs: pace.recentSpanMs,
  };
}

function shortName(upn: string): string {
  return upn.split('@')[0];
}

function pct(v: number): string {
  return `${Math.round(v)}%`;
}

export function seatCountText(n: number): string {
  return n === 1 ? '1 seat' : `${n} seats`;
}

function accountAlerts(a: AccountSignal, nowMs: number): Alert[] {
  const out: Alert[] = [];
  for (const w of a.windows) {
    if (w.resetAtMs <= nowMs) continue;
    const p = projectWindow(w, nowMs);
    if (p.level === null) continue;
    const name = shortName(a.accountUpn);
    const resetIn = formatDuration(p.resetAtMs - nowMs);
    const seats = a.seatIds.length
      ? ` · ${seatCountText(a.seatIds.length)} will stall`
      : '';
    const title =
      p.level === 'crit'
        ? p.percent >= 100
          ? `${name} is at its ${w.window} limit`
          : `${name} hits its ${w.window} limit in about ${formatDuration((p.hitsAtMs ?? nowMs) - nowMs)}`
        : `${name} on pace for ${pct(p.projectedPct ?? p.percent)} of its ${w.window} limit`;
    // "Projected at reset" labels the weekly figure as a projection (WI 34543).
    const projectedSeg =
      w.window === '7d' && p.projectedPct !== null
        ? `, projected at reset: ${pct(p.projectedPct)}`
        : '';
    out.push({
      id: `account|${a.accountUpn.toLowerCase()}|${w.window}`,
      severity: p.level,
      kind: 'account',
      category: 'account',
      title,
      detail: `${pct(p.percent)} used${projectedSeg}, resets in ${resetIn} · ${rateBasisText(p)}${p.level === 'crit' ? seats : ''}`,
      action: {
        type: 'view-account',
        label: 'View account',
        target: a.accountUpn,
      },
      weight: p.projectedPct ?? p.percent,
    });
  }
  return out;
}

function seatAlerts(s: SeatSignal): Alert[] {
  const where = s.host ?? 'unknown host';
  if (s.state === 'logged-out') {
    return [
      {
        id: `seat|${s.instanceId}|down`,
        severity: 'crit',
        kind: 'seat',
        category: 'down',
        title: `${s.instanceId} is logged out`,
        detail: `${where} · ${s.stateDetail ?? 'login needed'} · no work possible`,
        action: { type: 'log-in', label: 'Log in', target: s.instanceId },
        weight: 1000,
      },
    ];
  }
  if (s.state === 'limit-reached') {
    return [
      {
        id: `seat|${s.instanceId}|down`,
        severity: 'crit',
        kind: 'seat',
        category: 'down',
        title: `${s.instanceId} hit its usage limit`,
        detail: `${where} · ${s.stateDetail ?? 'usage limit reached'} · no work until the window resets`,
        action: s.account
          ? { type: 'view-account', label: 'View account', target: s.account }
          : { type: 'open-seat', label: 'Open seat', target: s.instanceId },
        weight: 950,
      },
    ];
  }
  const waitedMs = (s.idleMinutes ?? 0) * 60_000;
  if (s.state === 'prompt' && waitedMs >= PROMPT_STUCK_MS) {
    return [
      {
        id: `seat|${s.instanceId}|down`,
        severity: 'crit',
        kind: 'seat',
        category: 'down',
        title: `${s.instanceId} stuck on a ${promptLabel(s.stateDetail)}`,
        detail: `${where} · waiting ${formatDuration(waitedMs)} with no progress`,
        action: { type: 'open-seat', label: 'Open seat', target: s.instanceId },
        weight: 900 + (s.idleMinutes ?? 0),
      },
    ];
  }
  if (s.ctxPct !== null && s.ctxPct > CONTEXT_WARN_PCT) {
    return [
      {
        id: `seat|${s.instanceId}|context`,
        severity: s.ctxPct >= CONTEXT_CRIT_PCT ? 'crit' : 'warn',
        kind: 'seat',
        category: 'context',
        title: `${s.instanceId} at ${pct(s.ctxPct)} context`,
        detail: where,
        action: {
          type: 'cycle-seat',
          label: 'Cycle seat',
          target: s.instanceId,
        },
        weight: s.ctxPct,
      },
    ];
  }
  return [];
}

function promptLabel(detail: string | null): string {
  switch (detail) {
    case 'permission':
      return 'permission prompt';
    case 'ask-user-question':
      return 'question';
    case 'trust-folder':
      return 'trust-folder dialog';
    case 'codex-approval':
      return 'command approval';
    default:
      return 'prompt';
  }
}

/** True when a host with seats has gone quiet past the liveness window. */
export function isSilent(h: HostSignal, nowMs: number): boolean {
  return (
    h.seatIds.length > 0 &&
    (h.lastReportAtMs === null || nowMs - h.lastReportAtMs > HOST_LIVENESS_MS)
  );
}

function hostAlerts(h: HostSignal, nowMs: number): Alert[] {
  const seats = seatCountText(h.seatIds.length);
  const open: AlertAction = {
    type: 'open-machine',
    label: 'Open machine',
    target: h.host,
  };
  if (isSilent(h, nowMs)) {
    const age =
      h.lastReportAtMs === null
        ? 'has never reported'
        : `stopped reporting ${formatDuration(nowMs - h.lastReportAtMs)} ago`;
    return [
      {
        id: `machine|${h.host}|silent`,
        severity: 'crit',
        kind: 'machine',
        category: 'machine',
        title: `${h.host} ${age}`,
        detail: `${seats} on it: ${h.seatIds.join(', ')}`,
        action: open,
        weight: 800,
      },
    ];
  }
  const out: Alert[] = [];
  const meters: [string, number | null][] = [
    ['disk', h.diskPct],
    ['memory', h.memPct],
  ];
  for (const [label, v] of meters) {
    if (v === null || v < HOST_WARN_PCT) continue;
    const free =
      label === 'disk' && h.diskFreeBytes !== null
        ? `${Math.round(h.diskFreeBytes / 1024 ** 3)} GB free · `
        : '';
    out.push({
      id: `machine|${h.host}|${label}`,
      severity: v >= HOST_CRIT_PCT ? 'crit' : 'warn',
      kind: 'machine',
      category: 'machine',
      title: `${h.host} ${label} ${pct(v)}${label === 'disk' ? ' full' : ''}`,
      detail: `${free}${seats} on it`,
      action: open,
      weight: v,
    });
  }
  return out;
}

const CATEGORY_RANK: Record<AlertCategory, number> = {
  waiting: 0,
  down: 1,
  owed: 2,
  machine: 3,
  account: 4,
  context: 5,
};

/** Worst first: critical before warning, live before parked (a parked thread
 * is not waiting on anyone yet), then a user waiting, down, owed duty,
 * machine, account, context, then the bigger number. */
export function compareAlerts(a: Alert, b: Alert): number {
  return (
    (a.severity === b.severity ? 0 : a.severity === 'crit' ? -1 : 1) ||
    Number(!!a.parked) - Number(!!b.parked) ||
    CATEGORY_RANK[a.category] - CATEGORY_RANK[b.category] ||
    b.weight - a.weight ||
    a.id.localeCompare(b.id)
  );
}

export function buildAlerts(input: AttentionInput, nowMs: number): Alert[] {
  return [
    ...input.seats.flatMap((s) => seatAlerts(s)),
    ...input.hosts.flatMap((h) => hostAlerts(h, nowMs)),
    ...input.accounts.flatMap((a) => accountAlerts(a, nowMs)),
    ...waitingAlerts(input.waiting ?? [], seatSessionLinks(input.sessions)),
    ...daemonAlerts(input.daemons ?? []),
    ...seatLoginAlerts(input.seats),
    ...seatDownAlerts(input, nowMs),
    ...owedDutyAlerts(input.owed ?? [], nowMs),
  ].sort(compareAlerts);
}

// ── Tiles + filter ──────────────────────────────────────────────────────────

export type FocusKey = 'all' | 'you' | AlertCategory;

export interface Tile {
  key: Exclude<FocusKey, 'all'>;
  label: string;
  count: number;
  hint: string;
}

const TILE_DEFS: { key: Exclude<FocusKey, 'all'>; label: string }[] = [
  { key: 'you', label: 'Needs you' },
  { key: 'waiting', label: 'Users waiting > 2 min' },
  { key: 'down', label: 'Seats down' },
  { key: 'owed', label: 'Duties owed > 30 min' },
  { key: 'context', label: 'Seats over 60% context' },
  { key: 'account', label: 'Accounts near a limit' },
  { key: 'machine', label: 'Machine problems' },
];

/** The seven filter tiles. The hint names the worst item in each; a parked
 * thread is listed under Users waiting but not counted as a live wait. */
export function buildTiles(
  alerts: Alert[],
  needsYou: { title: string }[]
): Tile[] {
  return TILE_DEFS.map((d) => {
    if (d.key === 'you') {
      return {
        ...d,
        count: needsYou.length,
        hint: needsYou[0]?.title ?? 'nothing waiting on you',
      };
    }
    const items = alerts.filter((a) => a.category === d.key && !a.parked);
    return {
      ...d,
      count: items.length,
      hint: items[0]?.title ?? 'all clear',
    };
  });
}

/** Tapping the active tile again clears the filter. */
export function nextFocus(current: FocusKey, tapped: FocusKey): FocusKey {
  return current === tapped ? 'all' : tapped;
}

/** The Needs attention list under a focus. 'you' shows no alerts: the asks
 * live in the Needs you card. */
export function filterAlerts(alerts: Alert[], focus: FocusKey): Alert[] {
  if (focus === 'all') return alerts;
  if (focus === 'you') return [];
  return alerts.filter((a) => a.category === focus);
}
